import React, { useEffect, useMemo, useRef } from 'react'

import { format as formatDate } from 'date-fns'
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik'
import { toast } from 'react-toastify'

import { RichAddressInput } from '../../ManualApplication/components/AddressInput'
import { PROPERTY_TYPES, PROPERTY_USAGES, US_STATES } from '../../ManualApplication/constants'
import { getCreateApplicationPayload } from '../../ManualApplication/helpers/user'
import { validationSchema } from '../../ManualApplication/validationSchema'
import { RichEmailInput } from '../components/EmailInput'
import { setManualApplicationDrawerOpen } from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { InputField, SelectField } from '@/components/FormikInputs'
import Stepper, { Step } from '@/components/Stepper'
import { USTimezonesByState } from '@/constants/USTimezonesByState'
import { RedFlagIcon } from '@/features/AlertFlag/RedFlagIcon'
import { useGetConstsQuery, useImportApplicationDataMutation } from '@/services/application'
import {
  useCheckDuplicatedAddressMutation,
  useCreateApplicationMutation,
  useSignUpMutation,
} from '@/services/customer/userApi'
import { useLazyGetProfileFromUserQuery } from '@/services/profile'
import { useLazyGetUserApplicationsQuery } from '@/services/property'
import { useLazyGetAllStatesQuery } from '@/services/states'
import { IAddress, IUser } from '@/types/services'

interface IFormValues {
  street: string
  city: string
  state: string
  zip_code: string
  property_type: string | null
  property_usage: string
  use_of_proceeds: Array<{ value: string; label: string }>
  first_name: string
  last_name: string
  email: string
  phone: string
  customerType: string
  newUser: boolean
  userFound: boolean
  propertySelected: number
  sourceApplication: string
  userApplications: Array<{
    uuid: string
    property: { address: IAddress }
    application_state: string
    application_state_label: string
    created_at: string
    loss_type_label?: string
    loss_reason_label?: string
  }>
  applicationCreationError: string
  selectedUser: boolean
  selectedUserEmail: string
}

export function ManualApplicationFormikContext() {
  const { values, validateForm, setFieldValue } = useFormikContext<Partial<IFormValues>>()
  useEffect(() => {
    validateForm()
  }, [values?.street, values?.city, values?.state, values?.zip_code])

  useEffect(() => {
    if (values?.email !== values?.selectedUserEmail && values?.selectedUserEmail !== '') {
      setFieldValue('userFound', false)
      setFieldValue('userReachedLimit', false)
      setFieldValue('selectedUser', false)
      setFieldValue('propertySelected', 2)
      setFieldValue('sourceApplication', '')
      setFieldValue('userApplications', [])
      setFieldValue('newUser', true)
      setFieldValue('first_name', '')
      setFieldValue('last_name', '')
      setFieldValue('phone', '')
      setFieldValue('propertySelected', 2)
      setFieldValue('street', '')
      setFieldValue('city', '')
      setFieldValue('state', '')
      setFieldValue('zip_code', '')
      setFieldValue('property_type', null)
      setFieldValue('property_usage', '')
      setFieldValue('selectedUserEmail', '')
    }
  }, [values?.email])

  return null
}

const ManualApplication = () => {
  const dispatch = useAppDispatch()

  const [signup] = useSignUpMutation()
  const [getProfileFromUserQuery] = useLazyGetProfileFromUserQuery()
  const [getUserApplicationsQuery] = useLazyGetUserApplicationsQuery()
  const [checkDuplicatedAddress] = useCheckDuplicatedAddressMutation()
  const [createApplication] = useCreateApplicationMutation()
  const [importApplicationData] = useImportApplicationDataMutation()
  const [getStates] = useLazyGetAllStatesQuery()
  const { data: consts } = useGetConstsQuery()
  const formRef = useRef(null)

  const manualApplicationDrawerStep = useAppSelector(
    (state) => state.general.manualApplicationDrawerStep
  )

  const initialValues = useMemo(() => {
    return {
      street: '',
      city: '',
      state: '',
      zip_code: '',
      property_type: 'single-family-detached',
      property_usage: 'principal-residence',
      use_of_proceeds: [],
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      customerType: '',
      newUser: true,
      userFound: false,
      userReachedLimit: false,
      propertySelected: 2,
      sourceApplication: '',
      userApplications: [],
      applicationCreationError: '',
      selectedUser: false,
      selectedUserEmail: '',
    }
  }, [])

  const handleSearchEmailSuccessful =
    (setFieldValue: (field: string, value, shouldValidate?: boolean) => void) =>
    async (newUser: IUser) => {
      const { data: userProfiles } = await getProfileFromUserQuery(newUser.id)
      setFieldValue('first_name', newUser.first_name)
      setFieldValue('last_name', newUser.last_name)
      setFieldValue('phone', userProfiles[0]?.phone) // Customer phone is stored on profile
      setFieldValue('userFound', true)
      setFieldValue('selectedUser', true)
      setFieldValue('propertySelected', -1)
      const userApplications = await getUserApplicationsQuery(newUser.data.external_id).unwrap()
      const userReachedLimit =
        userApplications?.some(
          (application) =>
            application.aggregate_investment_payment >=
            consts?.DEFAULT_MAX_INVESTMENT - consts?.DEFAULT_MIN_INVESTMENT
        ) && userApplications?.some((application) => application.application_state === 'closed-won')
      setFieldValue('userReachedLimit', userReachedLimit)
      setFieldValue('userApplications', userApplications)
      setFieldValue('newUser', false)
      setFieldValue('selectedUserEmail', newUser.email)
    }

  const goToApplication = (external_id: string) => {
    window.open(`/applications/${external_id}/summary`, '_blank')?.focus()
  }

  const handleSearchSuccessful =
    (setFieldValue: (field: string, value, shouldValidate?: boolean) => void) =>
    async (newAddress: IAddress) => {
      const originatingStates = await getStates({ status: 'Originating' }).unwrap()
      const originatinStatesAbbr =
        originatingStates?.results?.map((s) => s.state_abbreviation) ?? []
      validationSchema.fields.state = validationSchema.fields.state.oneOf(
        originatinStatesAbbr,
        'Unlock does not offer agreements in this State'
      )
      setFieldValue('city', newAddress.city)
      setFieldValue('state', newAddress.state, true)
      setFieldValue('zip_code', newAddress.zip_code)
      setFieldValue('propertySelected', 1)
      // Triggers validation on state field
      formRef.current.setFieldTouched('state', true)
    }

  const getSteps = (values, errors, setFieldValue) => {
    const steps: Step[] = [
      {
        title: 'Applicant’s Email Address',
        content: (_, setCurrentStep) => (
          <div className='w-full h-full items-center'>
            <RichEmailInput
              label=''
              name='email'
              placeholder=''
              autoFocus
              handleSearchSuccessful={handleSearchEmailSuccessful(setFieldValue)}
              className='w-3/4'
            />
            {values.userFound && (
              <>
                <div className='mt-4'>
                  There are {values.userApplications.length} existing properties linked to this
                  email address. Choose a property address to use or enter a new one:
                </div>

                <div className='mt-2'>
                  {' '}
                  {/* Container for radio select options */}
                  {values.userApplications.map((application, index) => (
                    <label
                      key={application.uuid}
                      className='flex items-center py-2 cursor-pointer' // Set cursor to pointer
                      htmlFor={`property-${index}`}
                      data-test='manuallycreatemaxdeal-existingpropertyfound-btn'
                    >
                      <input
                        type='radio'
                        name='propertySelection'
                        id={`property-${index}`}
                        value={application.uuid}
                        className='mr-2'
                        checked={values.sourceApplication === application.uuid}
                        onChange={() => {
                          setFieldValue('city', application.property.address.city)
                          setFieldValue('state', application.property.address.state)
                          setFieldValue('zip_code', application.property.address.zip_code)
                          setFieldValue('street', application.property.address.street)
                          setFieldValue('propertySelected', 1)
                          setFieldValue('sourceApplication', application.uuid)
                          setFieldValue('property_type', application.property.property_type || '')
                          setFieldValue('property_usage', application.property.property_usage || '')
                          setCurrentStep((currentStep) => currentStep + 1)
                        }}
                      />
                      <div className='text-sm font-light'>
                        <span className='text-blue-800 underline'>
                          {application.property.address.street} {application.property.address.city},{' '}
                          {application.property.address.state} -{' '}
                          {application.property.address.zip_code}
                        </span>{' '}
                        <div className='text-xs'>
                          {formatDate(new Date(application.created_at), 'MM/dd/yy')}{' '}
                          <span
                            className={`ml-2 ${
                              application.application_state === 'closed-won'
                                ? 'text-green-400'
                                : application.application_state === 'closed-lost'
                                  ? 'text-red-400'
                                  : ''
                            }`}
                          >
                            {application.application_state_label}
                            {application.application_state === 'closed-lost' &&
                              ` - ${application.loss_type_label}: ${application.loss_reason_label}`}
                          </span>
                        </div>
                      </div>
                      {values.userReachedLimit && index === 0 && (
                        <div
                          className='tooltip'
                          data-tip='This customer has reached the maximum investment threshold of $500k. No new deals can be created.'
                        >
                          <RedFlagIcon />
                        </div>
                      )}
                    </label>
                  ))}
                  <label
                    className='flex items-center py-2 cursor-pointer'
                    htmlFor='new-property'
                    data-test='manuallycreatemaxdeal-enternewaddres-btn'
                  >
                    <input
                      type='radio'
                      name='propertySelection'
                      id='new-property'
                      value='new'
                      className='mr-2'
                      checked={values.propertySelected === 2}
                      onChange={() => {
                        setFieldValue('propertySelected', 2)
                        setFieldValue('street', '')
                        setFieldValue('city', '')
                        setFieldValue('state', '')
                        setFieldValue('zip_code', '')
                        setFieldValue('property_type', null)
                        setFieldValue('property_usage', '')
                        setCurrentStep((currentStep) => currentStep + 1)
                      }}
                    />
                    <span className='text-sm font-light text-blue-800 underline'>
                      Enter a new address
                    </span>
                  </label>
                </div>
              </>
            )}
          </div>
        ),
        buttonLabel: 'Next',
        disabledButton: errors?.email || values?.propertySelected === -1,
      },
      {
        title: 'Applicant Details',
        content: () => (
          <div className='w-full h-full items-center'>
            <InputField
              name={`first_name`}
              data-test='manuallycreatemaxdeal-application-firstname-input'
              style='default'
              placeholder='Applicant First Name'
              type='text'
              className='w-full !text-black'
              parentClassName='my-4'
              showError
            />
            <InputField
              name={`last_name`}
              data-test='manuallycreatemaxdeal-application-lastname-input'
              style='default'
              placeholder='Applicant Last Name'
              type='text'
              className='w-full !text-black'
              parentClassName='my-4'
              showError
            />
            <InputField
              name={`phone`}
              data-test='manuallycreatemaxdeal-telephone-input'
              style='default'
              placeholder='Telephone'
              type='text'
              className='w-full !text-black'
              parentClassName='mt-4'
              showError
            />
          </div>
        ),
        disabledButton: errors?.first_name || errors?.last_name || errors?.phone,
        buttonLabel: 'Next',
      },
      {
        title: 'Address',
        content: () => (
          <div className='w-full h-full items-center'>
            <RichAddressInput
              label=''
              name='street'
              placeholder='Property Address'
              handleSearchSuccessful={handleSearchSuccessful(setFieldValue)}
              style='classic'
            />
            <InputField
              name={`city`}
              data-test='manuallycreatemaxdeal-city-input'
              style='default'
              placeholder='City'
              type='text'
              className='w-full !text-black'
              parentClassName='my-4'
              showError
            />
            <InputField
              name={`state`}
              data-test='manuallycreatemaxdeal-state-input'
              style='default'
              placeholder='State'
              type='text'
              className='w-full !text-black'
              parentClassName='my-4'
              showError
            />
            <InputField
              name={`zip_code`}
              data-test='manuallycreatemaxdeal-zipcode-input'
              style='default'
              placeholder='Zip Code'
              type='text'
              className='w-full !text-black'
              parentClassName='mt-4'
              showError
            />
          </div>
        ),
        buttonLabel: 'Next',
        disabledButton: errors?.street || errors?.city || errors?.state || errors?.zip_code,
      },
      {
        title: 'Property Parameters',
        content: () => (
          <div className='w-full h-full items-center'>
            <SelectField
              label=''
              name='property_type'
              data-test='manuallycreatemaxdeal-propertytype-selectfield'
              style='default'
              extraClasses='w-full my-4'
              placeholder='Property Type'
              options={PROPERTY_TYPES}
            />
            <SelectField
              label=''
              name='property_usage'
              placeholder='Property Usage'
              data-test='manuallycreatemaxdeal-propertyusage-selectfield'
              extraClasses='w-full mt-4'
              style='default'
              options={PROPERTY_USAGES}
            />
          </div>
        ),
        buttonLabel: 'Next',
        hideButton: true,
        disabledButton: errors?.property_type || errors?.property_usage || errors?.use_of_proceeds,
        disabledStep: errors?.state,
      },
    ]

    return steps
  }

  const createAccount = async (values: typeof initialValues, setFieldValue) => {
    const { first_name, last_name, email, phone, street, city, state } = values
    const { tz: timezone } = USTimezonesByState[state]
    const { has_application } = await checkDuplicatedAddress({
      street: street,
      city: city,
      state: state,
    }).unwrap()
    // If the request was successful but there is no data, then the user doesn't exist
    // (204 NO CONTENT).

    if (has_application) {
      await handleCreateApplication(values, setFieldValue)
    } else {
      await signup({
        first_name,
        last_name,
        email,
        phone,
        timezone,
        is_manually_created: true,
      }).unwrap()

      await handleCreateApplication(values, setFieldValue)
    }
  }

  const handleOnSubmit = async (
    values: typeof initialValues,
    { setFieldValue, setSubmitting }: FormikHelpers<typeof initialValues>
  ) => {
    try {
      setSubmitting(true)
      setFieldValue('applicationCreationError', '')
      const { newUser } = values
      if (!newUser) await handleCreateApplication(values, setFieldValue, false)
      else await createAccount(values, setFieldValue)
      setSubmitting(false)
      toast.success('The application was created successfully.', {
        className: 'automation-toast-popup-success',
      })
      dispatch(setManualApplicationDrawerOpen(false))
    } catch (error) {
      toast.error('An error occurred while creating the application. Please try again.', {
        className: 'automation-toast-popup-error',
      })
    }
  }

  const handleCreateApplication = async (values, setFieldValue, newUser = true) => {
    const {
      first_name,
      last_name,
      email,
      phone,
      street,
      city,
      state,
      property_usage,
      property_type,
      use_of_proceeds,
      zip_code,
      sourceApplication,
    } = values
    const address: IAddress = {
      street: street,
      city: city,
      state: state,
      zip_code: zip_code,
      statename: US_STATES.find((usState) => usState.value == state)?.label,
    }

    const completeUserInput = {
      address,
      property_type: property_type ? property_type : null,
      property_usage,
      use_of_proceeds: use_of_proceeds.map((use: { value: string; label: string }) => use.value),
      first_name,
      last_name,
      email,
      phone,
      consent_to_privacy: true,
      consent_to_terms: true,
      manually_created: true,
    }
    const { external_id } = await createApplication(
      getCreateApplicationPayload(completeUserInput, newUser)
    ).unwrap()
    if (sourceApplication)
      await importApplicationData({
        destination_application: external_id,
        source_application: sourceApplication,
      })
    goToApplication(external_id)
  }

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      validationSchema={validationSchema}
      validateOnMount
    >
      {({ values, setFieldValue, errors, isSubmitting, handleSubmit, isValid }) => {
        return (
          <Form
            spellCheck={false}
            autoComplete='off'
          >
            <Stepper
              initialStep={manualApplicationDrawerStep}
              steps={getSteps(values, errors, setFieldValue)}
              finishButton={{
                extraDisabled: !values.property_usage,
                label: values?.applicationCreationError
                  ? 'Retry - Finish/Create New Deal'
                  : 'Finish/Create New Deal',
                footerMessage: values?.applicationCreationError && (
                  <div className={`${values?.applicationCreationError && 'text-red-500'}`}>
                    {values?.applicationCreationError}
                  </div>
                ),
                onClick: () => handleSubmit(),
                loadingFooter: isSubmitting,
                extraEnabled: (currentStep) => isValid && currentStep !== 1,
              }}
            />
            <ManualApplicationFormikContext />
          </Form>
        )
      }}
    </Formik>
  )
}

export default ManualApplication
